import {
  Component,
  computed,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { ApiService } from '../../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Socket } from 'ngx-socket-io';
import {
  TAGInvItemInstance,
  TAGInvItemType,
  TAGInvTransaction,
} from '../../../../models/taginventory';
import { MessageService } from 'primeng/api';
import { PrinterService } from '../../../printer.service';
import {
  BarcodeScannerSearchField,
} from '../../../components/barcode-scanner-search-field/barcode-scanner-search-field';
import { MatIcon } from '../../../components/mat-icon/mat-icon';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../../components/action-confirmation/action-confirmation-dialog';
import {
  newProgressDialogStep,
  ProgressDialogStep,
  ProgressDialogStepState,
  openProgressDialog,
} from '../../../components/progressDialog/progressDialog';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ReactiveIconTextButton } from '../../../components/reactive-icon-text-button/reactive-icon-text-button';
import { dialogConfig } from '../../../dialogs';
import {
  TransactionEditDialog,
  TransactionEditDialogData,
} from '../transaction-edit-dialog/transactionEditDialog';
import { BarcodeEditDialog, BarcodeEditDialogData } from '../barcode-edit-dialog/barcodeEditDialog';
import { ItemHistoryDialog, ItemHistoryDialogData } from '../item-history-dialog/itemHistoryDialog';

export interface ItemEditDialogData {
  item: TAGInvItemType;
  selectedInstances?: string[];
  socket: Socket;
}

@Component({
  selector: 'app-internal-item-edit-dialog',
  imports: [
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    TranslatePipe,
    MatIcon,
    SelectModule,
    CheckboxModule,
    FormsModule,
    ReactiveIconTextButton,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './itemEditDialog.html',
  styleUrl: './itemEditDialog.scss',
})
export class ItemEditDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  public api = inject(ApiService);
  public printer = inject(PrinterService);

  @ViewChild(BarcodeScannerSearchField) search: BarcodeScannerSearchField;

  categories = signal<string[]>([]);
  loadingCategories = signal(true);

  transactions = signal<TAGInvTransaction[]>([]);
  loadingTransactions = signal(true);

  item = signal<TAGInvItemType>(new TAGInvItemType());

  showDeletedInstances = signal(false);

  selectedInstances = signal<string[]>([]);

  editingDisabled = signal(false);
  deletionDisabled = signal(false);

  itemForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    additionalInfo: new FormControl('', []),
    description: new FormControl('', []),
    category: new FormControl(undefined, []),
  });

  filteredInstances = computed(() => {
    return this.item().instances.filter(
      (instance) => !instance.deleted || this.showDeletedInstances(),
    );
  });

  constructor(
    public dialogRef: DynamicDialogRef<ItemEditDialog>,
    public config: DynamicDialogConfig<ItemEditDialogData>,
  ) {
    // Init new Item
    this.item.set(new TAGInvItemType());
    this.editingDisabled.set(!this.api.hasPermission('create_inventory_item'));
    this.deletionDisabled.set(!this.api.hasPermission('delete_inventory_item'));

    config.header = this.translate.translate('internal.inventory.itemEdit.titleNew', this.item())();

    if (config.data.item) {
      console.log(config.data.item.category);
      // Item has been given
      this.item.set(new TAGInvItemType(config.data.item.serialize()));
      if (this.item()._id) {
        this.editingDisabled.set(!this.api.hasPermission('edit_inventory_item'));
        config.header = this.translate.translate(
          'internal.inventory.itemEdit.title',
          this.item(),
        )();
      }
    }

    this.itemForm.patchValue(this.item().serialize());

    // Preselect Instances
    if (config.data.selectedInstances) {
      this.selectedInstances.set(config.data.selectedInstances);

      var hasDeletedSelected = false;
      for (let instanceID of this.selectedInstances()) {
        if (this.item().instances.find((instance) => instance.itemid == instanceID).deleted)
          hasDeletedSelected = true;
      }

      if (hasDeletedSelected) this.showDeletedInstances.set(true);
    }

    if (this.item().deleted) {
      this.editingDisabled.set(true);
      this.deletionDisabled.set(true);
    }

    /* if (this.editingDisabled()) {
      this.catControl.disable();
    } */
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.loadCategories();

      this.api
        .getInvTransactionsByItem(this.item()._id)
        .then((invTransactions: TAGInvTransaction[]) => {
          invTransactions.sort((a, b) => {
            return a.startdate > b.startdate ? -1 : 1;
          });

          this.transactions.set(invTransactions);
          this.loadingTransactions.set(false);

          this.initSocketHandler();
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotLoadInventoryTransactions')(),
            detail: reason.message,
          });
        });
    });
  }

  ngOnDestroy() {
    this.config.data.socket.removeListener(
      'updateTransaction',
      this.socket_onUpdateTransaction.bind(this),
    );
    this.config.data.socket.removeListener(
      'createTransaction',
      this.socket_onCreateTransaction.bind(this),
    );
  }

  initSocketHandler() {
    this.config.data.socket.on('updateTransaction', this.socket_onUpdateTransaction.bind(this));
    this.config.data.socket.on('createTransaction', this.socket_onCreateTransaction.bind(this));
  }

  socket_onUpdateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    this.transactions.update((t) => {
      t.splice(
        t.findIndex((t) => t._id == newTrans._id),
        1,
        newTrans,
      );
      return [...t];
    });
  }

  socket_onCreateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    this.transactions.update((t) => {
      t.unshift(newTrans);
      t.sort((a, b) => {
        return a.startdate > b.startdate ? -1 : 1;
      });
      return [...t];
    });
  }

  allItemsSelected() {
    return this.selectedInstances().length == this.item().instances.length;
  }

  selectAll() {
    if (this.selectedInstances().length == this.item().instances.length) {
      this.selectedInstances.set([]);
    } else {
      this.selectedInstances.set(this.item().instances.map((instance) => instance.itemid));
    }
  }

  loadCategories() {
    this.loadingCategories.set(true);
    this.api
      .getInvItems()
      .then((result) => {
        this.categories.set([]);
        for (let item of result.filter((item) => !item.deleted)) {
          if (!this.categories().includes(item.category) && item.category !== "") {
            this.categories.update((c) => [...c, item.category]);
          }
        }

        this.categories.update((c) => [...c.sort()]);
        this.loadingCategories.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadInventoryCategories')(),
          detail: reason.message,
        });
        console.error(reason);
      });
  }

  getOpenTransaction(instanceID: string) {
    var filtered = this.transactions().filter(
      (transaction) => transaction.findItem(this.item()._id, instanceID) != null,
    );
    return filtered.find(
      (transaction) => transaction.findItem(this.item()._id, instanceID).back == null,
    );
  }

  instanceStatus(instanceID: string) {
    var transaction = this.getOpenTransaction(instanceID);
    return transaction == null;
  }

  addInstance() {
    var nextId = 1;
    for (let instance of this.item().instances) {
      if (parseInt(instance.itemid) >= nextId) nextId = parseInt(instance.itemid) + 1;
    }

    this.item.update((i) => {
      let newI = new TAGInvItemType(i.serialize());
      newI.instances.push(
        new TAGInvItemInstance({
          itemid: nextId.toString(),
        }),
      );
      return newI;
    });

    this.selectedInstances.update((si) => [...si, nextId.toString()]);
  }

  instanceSelected(instanceID: string, checked: boolean) {
    if (checked) {
      this.selectedInstances.update((si) => [...si, instanceID]);
    } else {
      this.selectedInstances.update((si) => si.filter((id) => id != instanceID));
    }

    console.log(this.selectedInstances);
  }

  editInstanceBarcodes(instance: TAGInvItemInstance) {
    const editBarcodeRef = this.dialogService.open(
      BarcodeEditDialog,
      dialogConfig<BarcodeEditDialogData>({
        closable: true,
        data: {
          barcodes: instance.barcodes,
        },
      }),
    );

    editBarcodeRef.onClose.subscribe((result) => {
      console.log(result);
      if (result) {
        this.item.update((i) => {
          let newI = new TAGInvItemType(i.serialize());
          newI.instances.find((inst) => inst.itemid == instance.itemid).barcodes = result;
          return newI;
        });
      }
    });
  }

  deleteInstance(instanceID: string) {
    var amount = this.item().instances.filter((instance) => instance.itemid == instanceID).length;

    if (this.item().instances.find((instance) => instance.itemid == instanceID).deleted) {
      this.item().instances.find((instance) => instance.itemid == instanceID).deleted = false;
      return;
    }

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.inventory.itemEdit.deleteInstanceTitle', {
          item: this.item(),
          amount,
        })(),
        this.translate.translate('internal.inventory.itemEdit.deleteInstanceMessage', {
          item: this.item(),
          amount,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        this.item.update((i) => {
          let newI = new TAGInvItemType(i.serialize());
          if (!newI._id) {
            newI.instances = newI.instances.filter((instance) => instance.itemid != instanceID);
          } else {
            newI.instances.find((instance) => instance.itemid == instanceID).deleted = true;
          }
          return newI;
        });
      }
    });
  }

  showTransaction(instance: TAGInvItemInstance) {
    var transaction = this.getOpenTransaction(instance.itemid);

    const transactionEditRef = this.dialogService.open(
      TransactionEditDialog,
      dialogConfig<TransactionEditDialogData>({
        closable: true,
        data: {
          transactionId: transaction._id,
          socket: this.config.data.socket,
        },
      }),
    );
  }

  showInstanceHistory(instance: TAGInvItemInstance) {
    const historyDialogRef = this.dialogService.open(
      ItemHistoryDialog,
      dialogConfig<ItemHistoryDialogData>({
        closable: true,
        data: {
          item: this.item(),
          instanceid: instance.itemid,
          socket: this.config.data.socket,
        },
      }),
    );
  }

  saveEdit(close: boolean = true): void {
    if (!this.itemForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.inventory.itemEdit.updateProgress')()),
    );

    if (!this.item()._id) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.inventory.itemEdit.createProgress')()),
      );
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      let newItem = this.item();
      newItem.deserialize(this.itemForm.value);

      if (newItem._id) {
        this.api
          .updateInvItem(newItem)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.loadCategories();
            if (close) this.dialogRef.close(result);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .newInvItem(newItem)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.loadCategories();
            if (close) this.dialogRef.close(result);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }

  deleteItem() {
    if (!this.item()._id) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.inventory.itemEdit.deleteTitle', {
          item: this.item(),
        })(),
        this.translate.translate('internal.inventory.itemEdit.deleteMessage', {
          item: this.item(),
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.inventory.itemEdit.deleteProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteInvItem(this.item()._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close({ item: this.item, delete: true });
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  printLabelsAll() {
    if (!this.printer.isConnected())
      this.printer
        .connectToPrinter()
        .then((result) => {
          this.printer.printItemLabels(
            this.item(),
            this.item().instances.filter((instance) =>
              this.selectedInstances().includes(instance.itemid),
            ),
          );
        })
        .catch((reason) => {
          console.error(reason);
        });
    else
      this.printer.printItemLabels(
        this.item(),
        this.item().instances.filter((instance) =>
          this.selectedInstances().includes(instance.itemid),
        ),
      );
  }
}
